/* eslint-disable import/no-named-as-default-member */
import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectGlobals } from 'redux/globals/selectors';
import {
  packagesPageableSelector,
  packagesLoadedSelector,
  packagesItemsSelector,
} from 'redux/packages/selectors';
import usePackages from 'redux/packages';
import useMarketplaces from 'redux/marketplaces';
import useStore from 'redux/store/index';
import { actions } from 'redux/packages/slice';
import { actions as paquerActions } from 'redux/paquers/slice';
import { actions as storeActions } from 'redux/store/slice';
import { marketplacesItemsSelector } from 'redux/marketplaces/selectors';
import { storeItemsSelector } from 'redux/store/selectors';
import dayjs from 'dayjs';
import { DEFAULT_PAGINATE } from 'constants/defaultValues';
import { isReadyForDeliveryStatus } from 'constants/packages';
import SITELINKS from 'constants/sitelinks';
import { TableDateTimeFormat } from 'constants/dateFormats';
import PaqueryTable from '@paquery-team/lib-table';
import ViewPackageButton from 'components/viewPackageButton';
import DownloadLabelButton from 'components/downloadLabelButton';
import externalCodeColumn from 'components/tables/columns';
import PaquerSettingsButton from 'components/paquerSettingsButton';
import usePaquers from 'redux/paquers';
import SizeIndicator from 'components/sizeIndicator';
import EditDropdownButton from 'components/editRecordButton/EditDropdownButton';
import {
  useMarketplaceOptions,
  useOnTransitStatusOptions,
  useStoreOptions,
} from 'hooks/useOptions';

const downloadLabelColumn = {
  render: (_, record) => (
    <DownloadLabelButton
      packageId={record.key}
      externalCode={record.externalCode}
      marketplaceId={record.ownerID}
    />
  ),
};

const destinationPersonNameColumn = {
  title: 'Nombre destinatario',
  dataIndex: 'destinationPersonName',
};

const destinationAddressColumn = {
  title: 'Destino',
  dataIndex: 'destinationAddress',
};

const deliveryTermColumn = {
  title: 'Plazo',
  dataIndex: 'deliveryTermDescription',
};

const arrivedAtPaqueryPointDateColumn = {
  title: 'Arribó a PaqueryPoint',
  dataIndex: 'arrivedAtPaqueryPointDate',
  align: 'center',
};

const statusColumn = {
  title: 'Estado',
  dataIndex: 'statusDescription',
};

const sizeColumn = {
  title: 'Tamaño',
  dataIndex: 'packageSizeDescription',
  align: 'center',
  render: (text) => <SizeIndicator text={text[0]} />,
};

const viewPackageColumn = {
  align: 'center',
  render: (_, record) => <ViewPackageButton packageId={record.id} />,
};

const paquerSettingsColumn = {
  align: 'center',
  render: (_, record) => (
    <PaquerSettingsButton actions={actions} packageId={record} />
  ),
};

const dataColumns = [
  externalCodeColumn,
  destinationPersonNameColumn,
  destinationAddressColumn,
  deliveryTermColumn,
  sizeColumn,
  arrivedAtPaqueryPointDateColumn,
  statusColumn,
];

const PackageList = () => {
  usePackages();
  usePaquers();
  useStore();
  useMarketplaces({ initialize: true });
  const dispatch = useDispatch();
  const globals = useSelector(selectGlobals);
  const loaded = useSelector(packagesLoadedSelector);
  const items = useSelector(packagesItemsSelector);
  const pageable = useSelector(packagesPageableSelector);
  const marketplaces = useSelector(marketplacesItemsSelector);
  const marketplacesOptions = useMarketplaceOptions(marketplaces);
  const stores = useSelector(storeItemsSelector);
  const storesOptions = useStoreOptions(stores);
  const [selectedPackagesId, setSelectedPackagesId] = useState([]);
  const filteredStatus = useOnTransitStatusOptions();

  useEffect(() => {
    dispatch(paquerActions.initial());
  }, [dispatch]);

  const editColumn = {
    align: 'center',
    render: (_, record) => (
      <EditDropdownButton
        record={record}
        link={`${SITELINKS.packages.list}/id`}
        onEditAddressSuccess={() => {
          setTimeout(() => {
            dispatch(actions.refreshPage());
          }, 1500);
        }}
      />
    ),
  };

  const actionColumns = [paquerSettingsColumn, viewPackageColumn, editColumn];

  const columnsLargeDevice = [
    downloadLabelColumn,
    ...dataColumns,
    ...actionColumns,
  ];

  const columnsMediumDevice = [
    downloadLabelColumn,
    externalCodeColumn,
    statusColumn,
    ...actionColumns,
  ];

  const columnsSmallDevice = [
    downloadLabelColumn,
    externalCodeColumn,
    ...actionColumns,
  ];

  const rowSelection = {
    type: 'checkbox',
    columnWidth: '30px',
    hideDefaultSelections: true,
    selectedRowKeys: selectedPackagesId,
    onChange: (selectedRowKeys) => {
      setSelectedPackagesId(selectedRowKeys);
    },
    getCheckboxProps: (record) => ({
      disabled: !record.isDeliverable,
      name: record.externalCode,
    }),
  };

  const searchCallback = useCallback(
    (value) => {
      dispatch(actions.updateSearch(value));
    },
    [dispatch],
  );

  const updatePaginate = useCallback(
    (page) => {
      dispatch(actions.updatePaginationPackages(page));
    },
    [dispatch],
  );

  const updateDate = useCallback(
    (value) => {
      dispatch(actions.updateDate(value));
    },
    [dispatch],
  );

  const searcher = {
    onSearching: searchCallback,
    placeholder: 'Código o destinatario',
    allowEmptySearch: true,
  };

  const selectCallback = useCallback(
    (value) => {
      dispatch(actions.updateStatus(value));
    },
    [dispatch],
  );
  const selectMarketplaceCallback = useCallback(
    (marketSelected) => {
      dispatch(actions.updateMarket(marketSelected));
      if (marketSelected) dispatch(storeActions.all(marketSelected));
    },
    [dispatch],
  );
  const selectStoreCallback = useCallback(
    (storeSelected) => {
      dispatch(actions.updateStore(storeSelected));
    },
    [dispatch],
  );
  const selectPackageTypeCallback = useCallback(
    (newPackageType) => {
      dispatch(actions.updatePackageType(newPackageType));
    },
    [dispatch],
  );
  const selectDeliveryTermCallback = useCallback(
    (newDeliveryTerm) => {
      dispatch(actions.updateDeliveryTerm(newDeliveryTerm));
    },
    [dispatch],
  );

  const selectors = [
    {
      onChange: selectMarketplaceCallback,
      placeholder: 'Marketplace',
      list: marketplacesOptions,
    },
    {
      onChange: selectStoreCallback,
      placeholder: 'Tienda',
      list: storesOptions,
    },
    {
      onChange: selectPackageTypeCallback,
      placeholder: 'Tipo de envio',
      list: globals.packages.type,
    },
    {
      onChange: selectCallback,
      placeholder: 'Estado',
      list: filteredStatus,
    },
    {
      onChange: selectDeliveryTermCallback,
      placeholder: 'Plazo',
      list: globals.deliveryTerm,
    },
  ];

  const rangePicker = {
    onDateSelection: updateDate,
    required: {
      value: true,
      message: 'La fecha es requerida',
    },
  };

  let filteredPackages = [];
  if (loaded && items) {
    filteredPackages = items.map((packet) => ({
      ...packet,
      key: packet.id,
      externalCode: packet.externalCode,
      destinationPersonName: packet.shippingScheduleDestination.name,
      destinationAddress:
        packet.shippingScheduleDestination.shippingAddress.addressDetail,
      deliveryTermDescription:
        packet.deliveryTerm &&
        globals.deliveryTerm.find((term) => term.value === packet.deliveryTerm)
          .description,
      packageSizeDescription: globals.packages.size.find(
        (packSize) => packSize.value === packet.packageSize,
      ).name,
      arrivedAtPaqueryPointDate: packet.arrivedAtPaqueryPointDate
        ? dayjs(packet.arrivedAtPaqueryPointDate)
            .tz()
            .format(TableDateTimeFormat)
        : null,
      statusDescription: globals.packages.status.find(
        (state) => state.value === packet.status,
      ).name,
      isDeliverable: isReadyForDeliveryStatus(packet.status),
    }));
  }
  return (
    <>
      <PaqueryTable
        rowSelection={rowSelection}
        loading={!loaded}
        header={{
          selectors,
          searcher,
          rangePicker,
          refresh: () => dispatch(actions.refreshPage()),
          title: 'En transito',
        }}
        usePackageRowColors
        onChangePaginate={updatePaginate}
        dataSource={filteredPackages}
        paginate={pageable || DEFAULT_PAGINATE}
        dataColumns={dataColumns}
        colsForSmallDevice={columnsSmallDevice}
        colsForMediumDevice={columnsMediumDevice}
        colsForLargeDevice={columnsLargeDevice}
      />
    </>
  );
};

export default PackageList;
